import React, { useRef, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Alert, CircularProgress, Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { dashboardPaperSx } from '../DashboardPanel';

const ChatBubble = ({ role, content, youLabel, assistantLabel }) => {
    const isUser = role === 'user';
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: 1.25,
            }}
        >
            <Box
                sx={{
                    maxWidth: '85%',
                    px: 1.75,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: isUser ? 'primary.main' : 'action.hover',
                    color: isUser ? 'primary.contrastText' : 'text.primary',
                    border: isUser ? 'none' : '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5, opacity: 0.8 }}>
                    {isUser
                        ? <PersonOutlineIcon sx={{ fontSize: 14 }} />
                        : <SmartToyOutlinedIcon sx={{ fontSize: 14 }} />}
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
                        {isUser ? youLabel : assistantLabel}
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {content}
                </Typography>
            </Box>
        </Box>
    );
};

const AsistentTestChat = ({
    t,
    theme,
    messages,
    input,
    onInputChange,
    onSend,
    loading,
    error,
}) => {
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, loading]);

    return (
        <Box
            sx={{
                ...dashboardPaperSx(theme),
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                height: { xs: 480, lg: '100%' },
                minHeight: 480,
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
                {t('pages.asistent.chatTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('pages.asistent.chatHint')}
            </Typography>

            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    minHeight: 200,
                }}
            >
                {messages.length === 0 && !loading && (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 280 }}>
                            {t('pages.asistent.chatHint')}
                        </Typography>
                    </Box>
                )}
                {messages.map((m, i) => (
                    <ChatBubble
                        key={i}
                        role={m.role}
                        content={m.content}
                        youLabel={t('pages.asistent.chatYou')}
                        assistantLabel={t('pages.asistent.chatAssistant')}
                    />
                ))}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                        <Box sx={{ px: 2, py: 1.5, borderRadius: 2, bgcolor: 'action.selected' }}>
                            <CircularProgress size={20} />
                        </Box>
                    </Box>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('pages.asistent.chatPlaceholder')}
                    value={input}
                    onChange={onInputChange}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && onSend()}
                    disabled={loading}
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                    }}
                />
                <Button
                    variant="contained"
                    onClick={onSend}
                    disabled={loading || !input.trim()}
                    endIcon={<SendIcon />}
                    sx={{ borderRadius: 2, px: 2.5, flexShrink: 0 }}
                >
                    {t('pages.asistent.sendButton')}
                </Button>
            </Box>
        </Box>
    );
};

export default AsistentTestChat;
